import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkDirective from 'remark-directive';
import remarkGfm from 'remark-gfm';
import { Link, useParams } from 'react-router-dom';
import { ThemeProvider, styled } from 'styled-components';

import { HeroParagraph } from '../theme/typography';
import { Breadcrumbs, BreadcrumbChevron, BreadcrumbLink } from './Breadcrumbs';
import { ContributorRow } from './ContributorRow';
import { FooterSection } from './FooterSection';
import { ServalActionItemBlock } from './ServalActionItemBlock';
import { SupportBlock } from './SupportBlock';
import { ContactBlock } from './ContactBlock';
import { Header } from './Header';
import { PageDescription } from './PageDescription';
import { PageTitle } from './PageTitle';
import { PageWrapper } from './PageWrapper';
import { StandardSection, StandardSectionContentContainer } from './StandardSection';
import { theme } from '../theme/aikya';
import guides from '../data/guides.json';
import { formatGuideDate } from '../utils/formatGuideDate';

const ArticleHeader = styled.div`
	margin-bottom: 2.5rem;
	max-width: 48rem;
`;

const ArticleTitle = styled(PageTitle)`
	@media only screen and (max-width: ${(props) => props.theme.breakpoints.second}) {
		${(props) => props.theme.typography.headingL};
	}
`;

const ArticleImage = styled.img`
	max-width: 100%;
	width: 100%;
`;

const LastUpdated = styled.time`
	${(props) => props.theme.typography.body2XS};
	color: ${(props) => props.theme.colors.neutral4};
	display: block;
	margin-bottom: 1.5rem;
`;

const remarkWideDirective = () => (tree) => {
	const visit = (node) => {
		if (node.type === 'containerDirective' && node.name === 'wide') {
			node.data = {
				...node.data,
				hName: 'div',
				hProperties: { className: ['wide-content'] }
			};
		}

		node.children?.forEach(visit);
	};

	visit(tree);
};

const ArticleBody = styled.article`
	max-width: none;
	width: 100%;

	& > :not(.wide-content) {
		max-width: 32rem;
	}

	.wide-content {
		max-width: none;
		width: 100%;
	}

	h1 {
		${(props) => props.theme.typography.headingL};
		margin: 2rem 0 0.75rem;
		text-wrap: balance;
	}

	h2 {
		${(props) => props.theme.typography.headingM};
		margin: 2rem 0 0.75rem;
		text-wrap: balance;
	}

	h3 {
		${(props) => props.theme.typography.headingS};
		margin: 1.75rem 0 0.5rem;
		text-wrap: balance;
	}

	h4, h5, h6 {
		${(props) => props.theme.typography.headingXS};
		margin: 1.5rem 0 0.5rem;
		text-wrap: balance;
	}

	p, li {
		${(props) => props.theme.typography.bodyXS};
		margin-bottom: 1.5rem;
		margin-top: 1.5rem;
	}

	p {
		${(props) => props.theme.typography.bodyXS};
	}

	ul, ol {
		padding-left: 0;
	}

	ul:not(.contains-task-list) {
		list-style: none;
	}

	ul:not(.contains-task-list) > li,
	ol > li {
		display: block;
		margin-top: 1.25rem;
		margin-bottom: 1.25rem;
		padding-left: 1.25rem;
		position: relative;
	}

	ul:not(.contains-task-list) > li::before {
		color: ${(props) => props.theme.colors.yellow4};
		content: "◆";
		font-size: 0.75rem;
		left: 0.05rem;
		position: absolute;
		top: -0.04em;
	}

	ol {
		counter-reset: guide-list-item;
		list-style: none;
	}

	ol > li {
		counter-increment: guide-list-item;
		padding-left: 1.75rem;
	}

	ol > li::before {
		color: ${(props) => props.theme.colors.yellow4};
		content: counter(guide-list-item) ".";
		font-weight: bold;
		left: 0;
		position: absolute;
	}

	li p {
		margin-bottom: 0.25rem;
		margin-top: 0;
	}

	li + li {
		margin-top: 0;
	}

	a {
		background-color: ${(props) => props.theme.colors.purple9};
		color: ${(props) => props.theme.colors.purple4};
		text-decoration: none;
		padding: 0 0.25rem;

		&:hover {
			color: ${(props) => props.theme.colors.purple3};
			background-color: ${(props) => props.theme.colors.purple8};
			text-decoration: none;
		}
	}

	blockquote {
		border-left: 1px solid ${(props) => props.theme.colors.neutral6};
		color: ${(props) => props.theme.colors.neutral4};
		margin: 1.5rem 0;
		padding-left: 1rem;
	}

	blockquote p {
		${(props) => props.theme.typography.body2XS};
	}

	blockquote p:first-child {
		margin-top: 0;
	}

	blockquote p:last-child {
		margin-bottom: 0;
	}

	hr {
		border: 0;
		border-top: 1px solid ${(props) => props.theme.colors.neutral7};
		margin: 2rem 0;
	}

	code, pre {
		font-family: 'Geist Mono', monospace;
	}

	p code, li code {
		background: ${(props) => props.theme.colors.neutral9};
		border-radius: 0.2rem;
		padding: 0.1rem 0.25rem;
	}

	pre {
		background: ${(props) => props.theme.colors.neutral3};
		border-radius: 0.5rem;
		color: ${(props) => props.theme.colors.neutral10};
		${(props) => props.theme.typography.bodyXS};
		margin: 1.5rem 0;
		overflow-x: auto;
		padding: 1rem 1.5rem;
	}

	pre code {
		background: transparent;
		padding: 0;
	}

	table {
		border-collapse: collapse;
		display: block;
		margin: 1.5rem 0;
		max-width: 100%;
		overflow-x: auto;
		width: 100%;
	}

	th, td {
		border: 1px solid ${(props) => props.theme.colors.neutral7};
		padding: 0.5rem 0.75rem;
		text-align: left;
	}

	th {
		${(props) => props.theme.typography.heading2XS};
	}

	ul.contains-task-list {
		list-style: none;
		padding-left: 0;
	}

	li.task-list-item {
		list-style: none;
	}

	li.task-list-item input {
		margin-right: 0.5rem;
	}

	img {
		border-radius: 0.25rem;
		display: block;
		height: auto;
		margin: 1.5rem 0;
		max-width: 100%;
	}

	.wide-content img,
	.wide-content pre {
		box-sizing: border-box;
		width: 100%;
	}
`;

export const GuideArticlePage = () => {
	const { slug } = useParams();
	const guide = guides.find((entry) => entry.slug === slug && entry.public);

	return (
		<ThemeProvider theme={theme}>
			<PageWrapper>
				<Header />
				{guide.image && <ArticleImage src={guide.image} alt={guide.imageAlt} />}
				<StandardSection>
					<StandardSectionContentContainer>
						{guide ? (
							<>
								<Breadcrumbs>
									<BreadcrumbLink to="/guides">Guides</BreadcrumbLink>
									<BreadcrumbChevron>&gt;</BreadcrumbChevron>
								</Breadcrumbs>
								<ArticleHeader>
									<ArticleTitle>{guide.title}</ArticleTitle>
									<ContributorRow contributorIds={[guide.author]} />
									<HeroParagraph>{guide.description}</HeroParagraph>
									<LastUpdated dateTime={guide.lastUpdated}>
											Last updated: {formatGuideDate(guide.lastUpdated)}
									</LastUpdated>
								</ArticleHeader>
								<ArticleBody>
									<ReactMarkdown remarkPlugins={[remarkDirective, remarkWideDirective, remarkGfm]}>
										{guide.content}
									</ReactMarkdown>
								</ArticleBody>
							</>
						) : (
							<>
								<PageTitle>Guide not found</PageTitle>
								<PageDescription>We don't have a guide like that. Is it possible you made a typo?</PageDescription>
								<Link to="/guides">Browse all guides</Link>
							</>
						)}
					</StandardSectionContentContainer>
				</StandardSection>
				<ServalActionItemBlock />
				<SupportBlock />
				<ContactBlock />
				<FooterSection />
			</PageWrapper>
		</ThemeProvider>
	);
};